import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useToastStore } from './toast'
import { useChatsStore } from './chats'
import { useTextTitleStore } from './textTitleStore'
import { useBackgroundStore } from './backgroundStore'
import type { Character, EditCharacter } from '@/types'
import type {
  UpdateUniverseDraftPayload,
} from '@/types/universe'
import type { EditAvatarModel } from '@/types/character'
import type { IBGMItem } from '@/types/audio'
import { generateChatFromReturnedUniverse } from '@/utils/generateChatFromUniverse'
import { type ReturnedUniverse, universeService } from '@/services/universe'
import type { ChatHistoryPayload } from '@/services/chat'
import type { GeneratedEventModel } from '@/types/event'
import { eventService } from '@/services/event'
import { useFetchState } from '@/composables/useFetchState'

export const useCreateUniverseStore = defineStore('createUniverse', () => {
  const toastStore = useToastStore()
  const chatStore = useChatsStore()
  const titleStore = useTextTitleStore()
  const backgroundStore = useBackgroundStore()

  const whatIf = ref<string>('')
  const basicSettingBackground = ref<string>('')
  const basicSettingScenario = ref<string>('')
  const universeTheme = ref<string[]>([])

  const eventsSuggestions = ref<GeneratedEventModel[]>([])
  const selectedEventSuggestion = ref(-1)

  const showPublishGuide = ref(false)
  function setShowPublishGuide(value: boolean) {
    showPublishGuide.value = value
  }

  // 用于获取第一次之后的chapter建议
  const chatHistory = ref<ChatHistoryPayload[]>([])
  function setChatHistory(newChatHistory: ChatHistoryPayload[]) {
    chatHistory.value = newChatHistory
  }

  // 正在编辑中的角色对象，增量
  const inEditCharacter = ref<EditCharacter | undefined>()

  function setInEditCharacter(char: EditCharacter) {
    inEditCharacter.value = char
  }

  function updateInEditCharacter(newChar: EditCharacter) {
    if (inEditCharacter.value === undefined) {
      console.error('Character not found')
      return
    }
    inEditCharacter.value = newChar
  }

  function clearInEditCharacter() {
    inEditCharacter.value = undefined
  }

  // 这里是configure页面点击generate后面的返回的数据
  const generatedUniverse = ref<ReturnedUniverse | undefined>()

  // 轮训刷新预览Sekai对象
  async function refreshDraft() {
    if (generatedUniverse.value?.universeId) {
      console.log('refresh universe', generatedUniverse.value?.universeId)
      const res = await universeService.getUniverseDraftById(
        generatedUniverse.value?.universeId,
      )
      setUniverseDraft(res)
    }
  }

  /**
   * this action will be conducted after click next after basic setting
   * @param newSekai
   */
  function setUniverseDraft(newSekai: ReturnedUniverse) {
    generatedUniverse.value = {
      ...newSekai,
    }
    whatIf.value = newSekai.whatIf
    basicSettingBackground.value = newSekai.background
    basicSettingScenario.value = newSekai.firstEventDescription || ''
  }

  async function generateEventSuggestion(retry?: boolean) {
    const res = await eventService.generateFirstEventSuggestion(whatIf.value, retry)
    console.log(res)
    if (res) {
      eventsSuggestions.value = res
    }
  }
  const bgm = ref<IBGMItem | null>(null)
  const setBgm = (newBgm: IBGMItem | null) => {
    bgm.value = newBgm
  }

  function updateChar(newChar: Character) {
    if (generatedUniverse.value?.chars) {
      const index = generatedUniverse.value.chars.findIndex(
        char => char.charId === newChar.charId,
      )
      generatedUniverse.value.chars[index] = newChar
    }
  }

  const uploadedAvatarPreview = ref<EditAvatarModel | undefined>()
  function setUploadedAvatarPreview(preview: EditAvatarModel | undefined) {
    uploadedAvatarPreview.value = preview
  }

  /**
   * The step of creating sekai
   * 0~3
   * Configure->Story line->Media->Character
   */
  const createStep = ref<number>(0)
  const { fetchState: creatingState } = useFetchState()
  const canPublish = ref<boolean>(false)
  function setCanPublish(val: boolean) {
    canPublish.value = val
  }

  async function nextStep(onPublished?: (chatId: string) => void) {
    if (createStep.value < 4) {
      createStep.value += 1
    }
    else {
      creatingState.value = 'waitRes'
      // publish the universe
      try {
        if (generatedUniverse.value === undefined) {
          console.error('Universe not generated')
          return
        }

        // put the title data to draft if there's one
        if (titleStore.povTitle?.text !== '') {
          const payload: UpdateUniverseDraftPayload = {
            highlight: titleStore.povTitle,
          }
          await universeService.updateUniverseDraftById(
            generatedUniverse.value.universeId,
            payload,
          )
        }

        const res = await universeService.publishUniverse(
          generatedUniverse.value?.universeId,
        )
        const newChat = generateChatFromReturnedUniverse(res)
        chatStore.setNewCreatedChat(newChat)
        setTimeout(() => {
          reset()
          backgroundStore.dispose()
          if (onPublished) {
            onPublished(newChat.chatId)
          }
        }, 1000)
        if (res) {
          // 保证两个toast不会重叠
          setTimeout(() => {
            toastStore.setToast('Your Sekai is successfully created')
          }, 1000)
        }
      }
      catch (err) {
        console.error(err)
        creatingState.value = 'failed'
        toastStore.setToast('Failed to publish your Sekai')
      }
    }
  }

  function prevStep() {
    if (createStep.value > 0) {
      createStep.value -= 1
    }
  }

  function setCreateStep(step: number) {
    createStep.value = step
  }

  function reset() {
    generatedUniverse.value = undefined
    whatIf.value = ''
    basicSettingBackground.value = ''
    basicSettingScenario.value = ''
    universeTheme.value = []
    eventsSuggestions.value = []
    selectedEventSuggestion.value = -1
    bgm.value = null
    chatHistory.value = []
    createStep.value = 0
    uploadedAvatarPreview.value = undefined
    titleStore.reset()
    creatingState.value = 'beforeReq'
    canPublish.value = false
  }

  return {
    refreshDraft,
    setUniverseDraft,

    whatIf,
    basicSettingBackground,
    basicSettingScenario,
    universeTheme,

    generatedUniverse,

    inEditCharacter,
    setInEditCharacter,
    updateInEditCharacter,
    clearInEditCharacter,
    updateChar,
    reset,

    bgm,
    setBgm,

    uploadedAvatarPreview,
    setUploadedAvatarPreview,

    createStep,
    nextStep,
    prevStep,
    setCreateStep,
    creatingState,
    canPublish,
    setCanPublish,

    chatHistory,
    setChatHistory,

    eventsSuggestions,
    generateEventSuggestion,
    selectedEventSuggestion,

    showPublishGuide,
    setShowPublishGuide,
  }
})
